import _ from 'lodash'
import { AntSkeleton, Icons, Progress, Text } from '@agro-club/agroclub-shared'
import { FC, FunctionComponent, ReactNode, useMemo } from 'react'
import { AButton } from 'views/components/Analytics'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { AButtonProps } from 'views/components/Analytics/AButton'
import { ListRequestParams } from 'modules/domain/types'
import { useSingleEntity } from 'modules/domain/common/hooks'
import { endpoints } from 'modules/endpoints'
import { SampleReactionStatus } from '../types'
import { stringify } from 'query-string'

const Container = styled.div`
  display: flex;
  gap: 10px;
`

const SmallPauseIcon = styled(Icons.IconPause)`
  width: 16px !important;
  height: 16px !important;
`
const BtnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 180px;
`
type BtnProps = {
  id: string
  color: AButtonProps['color']
  Icon: FunctionComponent
  children: ReactNode
  onClick: () => void
  isActive: boolean
}
const FilterBtn = ({ id, color, Icon, children, onClick, isActive }: BtnProps) => (
  <BtnWrapper>
    <AButton
      id={id}
      Icon={Icon}
      size="large"
      color={color}
      background={isActive ? undefined : 'none'}
      border={isActive ? 'none' : 'solid'}
      onClick={onClick}
    >
      {children}
    </AButton>
  </BtnWrapper>
)

const BtnSkeleton = () => <AntSkeleton.Button style={{ width: 180, borderRadius: 4, height: 56 }} />

const TextContent: FC<{ label: string; value: number }> = ({ label, value }) => (
  <>
    <Text as="span" color="primary">
      {label}:
    </Text>{' '}
    <Text as="span" typography="accentLarge">
      {value.toString()}
    </Text>
  </>
)

type Props = {
  listRequestParams: ListRequestParams
  updateFilterState?: any
}

type ReactionStats = {
  [SampleReactionStatus.norm]: number
  [SampleReactionStatus.paused]: number
  [SampleReactionStatus.expired]: number
}
export const SampleButtonFilters: FC<Props> = ({ listRequestParams, updateFilterState }) => {
  const { t } = useTranslation('samples')

  const url = useMemo(
    () =>
      `${endpoints.sampleReactionStats()}?${stringify(_.omit(listRequestParams.filter, 'request_reaction_status'))}`,
    [listRequestParams.filter],
  )
  const [progress, data] = useSingleEntity<ReactionStats>(url, false)

  const onClick = (value?: string) => {
    if (value === listRequestParams.filter.request_reaction_status) {
      updateFilterState({ request_reaction_status: undefined })
      return
    }
    updateFilterState({ request_reaction_status: value })
  }
  if (progress === Progress.WORK || !data)
    return (
      <Container>
        <BtnSkeleton />
        <BtnSkeleton />
        <BtnSkeleton />
      </Container>
    )

  return (
    <Container>
      <FilterBtn
        isActive={listRequestParams.filter.request_reaction_status === 'norm'}
        id="norm"
        Icon={Icons.IconDone}
        color="secondary"
        onClick={() => onClick('norm')}
      >
        <TextContent label={t('sampleReaction.norm')} value={data.norm} />
      </FilterBtn>
      <FilterBtn
        isActive={listRequestParams.filter.request_reaction_status === 'paused'}
        id="paused"
        Icon={SmallPauseIcon}
        color="notify"
        onClick={() => onClick('paused')}
      >
        <TextContent label={t('sampleReaction.paused')} value={data.paused} />
      </FilterBtn>
      <FilterBtn
        isActive={listRequestParams.filter.request_reaction_status === 'expired'}
        id="expired"
        Icon={SmallPauseIcon}
        color="destruct"
        onClick={() => onClick('expired')}
      >
        <TextContent label={t('sampleReaction.expired')} value={data.expired} />
      </FilterBtn>
    </Container>
  )
}
